
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import ProductCopilotChatSidebar from './ProductCopilotChatSidebar';
import ChatSession from './ChatSession';
import CopilotUsageMeter from './CopilotUsageMeter';
import ProductCopilotTour from './ProductCopilotTour';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { useAIUsage } from '@/hooks/useAIUsage';
import { useProductCopilotTour } from '@/hooks/useProductCopilotTour';
import { canUserSendCopilotMessage } from '@/services/subscriptionService';
import { supabase } from '@/integrations/supabase/client';

export type CopilotMode = 'free' | 'enhance' | 'template';

interface ProductCopilotInterfaceProps {
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
}

const ProductCopilotInterface: React.FC<ProductCopilotInterfaceProps> = ({
  onToggleSidebar,
  sidebarCollapsed = false
}) => {
  const { user } = useAuth();
  const { data: aiUsage } = useAIUsage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentChatId, setCurrentChatId] = useState<string | undefined>();
  const [usageInfo, setUsageInfo] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [currentMode, setCurrentMode] = useState<CopilotMode>('free');
  const { showTour, setShowTour } = useProductCopilotTour();
  
  // Get session from URL params
  const sessionId = searchParams.get('session');
  const promptId = searchParams.get('prompt');
  const mode = searchParams.get('mode') as CopilotMode;

  useEffect(() => {
    if (sessionId) {
      setCurrentChatId(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (mode && ['free', 'enhance', 'template'].includes(mode)) {
      setCurrentMode(mode);
    }
  }, [mode]);

  useEffect(() => {
    if (user) {
      checkUsageLimits();
      fetchUserProfile();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_tier')
        .eq('id', user.id)
        .single();
      
      if (!error && data) {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const checkUsageLimits = async () => {
    if (!user) return;
    
    try {
      const usage = await canUserSendCopilotMessage(user.id);
      setUsageInfo(usage);
    } catch (error) {
      console.error('Error checking usage limits:', error);
    }
  };

  const handleNewChat = () => {
    // Complete reset for new chat
    setCurrentChatId(undefined);
    setCurrentMode('free');
    setSearchParams({});
  };

  const handleSelectChat = (chatId: string) => {
    setCurrentChatId(chatId);
    setSearchParams({ session: chatId, mode: currentMode });
  };

  const handleModeChange = (newMode: CopilotMode) => {
    setCurrentMode(newMode);
    const params: any = {};
    if (currentChatId) params.session = currentChatId;
    if (newMode !== 'free') params.mode = newMode;
    setSearchParams(params);
  };

  return (
    <div className="flex h-full bg-white dark:bg-gray-900">
      {/* Mobile sidebar toggle */}
      <div className="md:hidden fixed top-20 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleSidebar}
          className="bg-white shadow-md"
        >
          <Menu className="h-4 w-4" />
        </Button>
      </div>

      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'hidden md:block' : 'block'} ${sidebarCollapsed ? 'md:w-16' : 'w-full md:w-80'} fixed md:relative z-40 h-full`}>
        <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
          {/* Chat Sidebar */}
          <div className="flex-1 min-h-0">
            <ProductCopilotChatSidebar
              onNewChat={handleNewChat}
              onSelectChat={handleSelectChat}
              currentChatId={currentChatId}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={onToggleSidebar}
              currentMode={currentMode}
            />
          </div>
          
          {/* Usage Meter */}
          {!sidebarCollapsed && usageInfo && (
            <div className="border-t">
              <CopilotUsageMeter
                usedCount={usageInfo.usedCount}
                limit={usageInfo.limit}
                percentUsed={usageInfo.percentUsed}
                canSend={usageInfo.canSend}
                subscriptionTier={userProfile?.subscription_tier || 'free'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full">
        <ChatSession
          sessionId={currentChatId}
          onToggleSidebar={onToggleSidebar}
          sidebarCollapsed={sidebarCollapsed}
          mode={currentMode}
          onModeChange={handleModeChange}
          key={currentChatId || 'new-chat'} // Force re-render on new chat
        />
      </div>

      {/* Mobile sidebar overlay */}
      {!sidebarCollapsed && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={onToggleSidebar}
        />
      )}

      {/* Tour Modal */}
      <ProductCopilotTour
        open={showTour}
        onOpenChange={setShowTour}
      />
    </div>
  );
};

export default ProductCopilotInterface;
